import { useState, useEffect } from 'react'
import { Eye, EyeOff, Save, Users, CreditCard, Image, Settings, UserPlus, Search, Plus, Minus, Gift, Calendar, Copy, Trash2 } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'

interface AdminStats {
  users: { total: number; verified: number }
  transactions: { total: number; revenue: number }
  images: { total: number; creditsUsed: number }
}

interface AdminSettings {
  initial_credits: string
  credit_price_cents: string
  image_generation_cost: string
  max_image_size_mb: string
  allowed_image_types: string
  default_prompt: string
}

interface User {
  id: string
  email: string
  credits: number
  is_verified: boolean
  created_at: string
}

interface CreditTransaction {
  id: string
  amount: number
  type: 'purchase' | 'usage' | 'bonus'
  description: string
  created_at: string
}

interface RedemptionCode {
  id: string
  code: string
  credits: number
  maxUses: number
  usedCount: number
  isActive: boolean
  description?: string
  createdBy: string
  expiresAt?: string
  createdAt: string
  updatedAt: string
}

export default function AdminPage() {
  const [adminToken, setAdminToken] = useState('')
  const [showToken, setShowToken] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [settings, setSettings] = useState<AdminSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState<'dashboard' | 'credits' | 'redemption'>('dashboard')
  
  // Credit management states
  const [searchEmail, setSearchEmail] = useState('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditDescription, setCreditDescription] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [creditLoading, setCreditLoading] = useState(false)
  const [userTransactions, setUserTransactions] = useState<CreditTransaction[]>([])
  const [showTransactions, setShowTransactions] = useState(false)

  // Redemption code management states
  const [redemptionCodes, setRedemptionCodes] = useState<RedemptionCode[]>([])
  const [redemptionLoading, setRedemptionLoading] = useState(false)
  const [newCodeCredits, setNewCodeCredits] = useState('')
  const [newCodeMaxUses, setNewCodeMaxUses] = useState('1')
  const [newCodeDescription, setNewCodeDescription] = useState('')
  const [newCodeExpiresAt, setNewCodeExpiresAt] = useState('')
  const [newCodeCount, setNewCodeCount] = useState('1')
  const [createCodeLoading, setCreateCodeLoading] = useState(false)
  const [selectedCodes, setSelectedCodes] = useState<string[]>([])
  const [redemptionPagination, setRedemptionPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  })

  useEffect(() => {
    // Check for stored admin token
    const storedToken = localStorage.getItem('adminToken')
    if (storedToken) {
      setAdminToken(storedToken)
      verifyAndLoadData(storedToken)
    }
  }, [])

  const verifyAndLoadData = async (token: string) => {
    try {
      setLoading(true)
      setError('')

      // Test admin token by fetching stats
      const response = await fetch('/api/admin/stats', {
        headers: {
          'X-Admin-Token': token
        }
      })

      if (!response.ok) {
        throw new Error('Invalid admin token')
      }

      const statsData = await response.json()
      
      const settingsResponse = await fetch('/api/admin/settings', {
        headers: {
          'X-Admin-Token': token
        }
      })

      const settingsData = await settingsResponse.json()

      if (statsData.success && settingsData.success) {
        setStats(statsData.data)
        setSettings(settingsData.data)
        setIsAuthenticated(true)
        localStorage.setItem('adminToken', token)
      }
    } catch (error) {
      console.error('Admin auth error:', error)
      setError('管理员令牌无效')
      setIsAuthenticated(false)
      localStorage.removeItem('adminToken')
    } finally {
      setLoading(false)
    }
  }

  const handleLogin = () => {
    if (!adminToken.trim()) {
      setError('请输入管理员令牌')
      return
    }
    verifyAndLoadData(adminToken)
  }

  const updateSetting = async (key: string, value: string) => {
    try {
      setError('')
      setSuccess('')

      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': adminToken
        },
        body: JSON.stringify({ key, value })
      })

      const result = await response.json()

      if (result.success) {
        setSuccess('设置已更新')
        setTimeout(() => setSuccess(''), 3000)
        // Refresh settings
        verifyAndLoadData(adminToken)
      } else {
        setError(result.error || '更新失败')
      }
    } catch (error) {
      setError('更新设置失败')
    }
  }

  const handleLogout = () => {
    setIsAuthenticated(false)
    setAdminToken('')
    setStats(null)
    setSettings(null)
    localStorage.removeItem('adminToken')
  }

  const searchUser = async () => {
    if (!searchEmail.trim()) {
      setError('请输入用户邮箱')
      return
    }

    try {
      setSearchLoading(true)
      setError('')

      const response = await fetch(`/api/admin/users/search?email=${encodeURIComponent(searchEmail)}`, {
        headers: {
          'X-Admin-Token': adminToken
        }
      })

      const result = await response.json()

      if (result.success && result.data) {
        setSelectedUser(result.data)
        setShowTransactions(false)
        setUserTransactions([])
      } else {
        setError(result.error || '用户不存在')
        setSelectedUser(null)
      }
    } catch (error) {
      setError('搜索用户失败')
      setSelectedUser(null)
    } finally {
      setSearchLoading(false)
    }
  }

  const loadUserTransactions = async () => {
    if (!selectedUser) return

    try {
      const response = await fetch(`/api/admin/users/${selectedUser.id}/transactions`, {
        headers: {
          'X-Admin-Token': adminToken
        }
      })

      const result = await response.json()

      if (result.success) {
        setUserTransactions(result.data)
        setShowTransactions(true)
      } else {
        setError(result.error || '加载交易记录失败')
      }
    } catch (error) {
      setError('加载交易记录失败')
    }
  }

  const loadRedemptionCodes = async (page: number = 1) => {
    try {
      setRedemptionLoading(true)
      setError('')

      const response = await fetch(`/api/admin/redemption-codes?page=${page}&limit=20`, {
        headers: {
          'X-Admin-Token': adminToken
        }
      })

      const result = await response.json()

      if (result.success) {
        setRedemptionCodes(result.data.codes)
        setRedemptionPagination(result.data.pagination)
      } else {
        setError(result.error || '加载兑换码失败')
      }
    } catch (error) {
      setError('加载兑换码失败')
    } finally {
      setRedemptionLoading(false)
    }
  }

  const createRedemptionCodes = async () => {
    if (!newCodeCredits.trim() || !newCodeMaxUses.trim() || !newCodeCount.trim()) {
      setError('请填写必填字段')
      return
    }

    const credits = parseInt(newCodeCredits)
    const maxUses = parseInt(newCodeMaxUses)
    const count = parseInt(newCodeCount)

    if (isNaN(credits) || credits <= 0) {
      setError('请输入有效的积分数量')
      return
    }

    if (isNaN(maxUses) || maxUses <= 0) {
      setError('请输入有效的使用次数')
      return
    }

    if (isNaN(count) || count <= 0 || count > 100) {
      setError('生成数量必须在1-100之间')
      return
    }

    try {
      setCreateCodeLoading(true)
      setError('')
      setSuccess('')

      const requestData: any = {
        credits,
        maxUses,
        count
      }

      if (newCodeDescription.trim()) {
        requestData.description = newCodeDescription.trim()
      }

      if (newCodeExpiresAt.trim()) {
        requestData.expiresAt = new Date(newCodeExpiresAt).toISOString()
      }

      const response = await fetch('/api/admin/redemption-codes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': adminToken
        },
        body: JSON.stringify(requestData)
      })

      const result = await response.json()

      if (result.success) {
        setSuccess(`成功生成 ${result.data.codes.length} 个兑换码`)
        setNewCodeCredits('')
        setNewCodeMaxUses('1')
        setNewCodeDescription('')
        setNewCodeExpiresAt('')
        setNewCodeCount('1')
        // Refresh the codes list
        loadRedemptionCodes(redemptionPagination.page)
      } else {
        setError(result.error || '生成兑换码失败')
      }
    } catch (error) {
      setError('生成兑换码失败')
    } finally {
      setCreateCodeLoading(false)
    }
  }

  const deactivateCode = async (codeId: string) => {
    try {
      setError('')
      setSuccess('')

      const response = await fetch(`/api/admin/redemption-codes/${codeId}/deactivate`, {
        method: 'PUT',
        headers: {
          'X-Admin-Token': adminToken
        }
      })

      const result = await response.json()

      if (result.success) {
        setSuccess('兑换码已禁用')
        loadRedemptionCodes(redemptionPagination.page)
      } else {
        setError(result.error || '禁用兑换码失败')
      }
    } catch (error) {
      setError('禁用兑换码失败')
    }
  }

  const batchDeactivateCodes = async () => {
    if (selectedCodes.length === 0) {
      setError('请选择要禁用的兑换码')
      return
    }

    try {
      setError('')
      setSuccess('')

      const response = await fetch('/api/admin/redemption-codes/batch-deactivate', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': adminToken
        },
        body: JSON.stringify({ codeIds: selectedCodes })
      })

      const result = await response.json()

      if (result.success) {
        setSuccess(`成功禁用 ${result.data.deactivatedCount} 个兑换码`)
        setSelectedCodes([])
        loadRedemptionCodes(redemptionPagination.page)
      } else {
        setError(result.error || '批量禁用失败')
      }
    } catch (error) {
      setError('批量禁用失败')
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setSuccess('已复制到剪贴板')
      setTimeout(() => setSuccess(''), 2000)
    } catch (error) {
      setError('复制失败')
    }
  }

  const adjustCredits = async (operation: 'add' | 'subtract') => {
    if (!selectedUser || !creditAmount.trim() || !creditDescription.trim()) {
      setError('请填写所有字段')
      return
    }

    const amount = parseInt(creditAmount)
    if (isNaN(amount) || amount <= 0) {
      setError('请输入有效的积分数量')
      return
    }

    const finalAmount = operation === 'add' ? amount : -amount

    try {
      setCreditLoading(true)
      setError('')
      setSuccess('')

      const response = await fetch(`/api/admin/users/${selectedUser.id}/credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Token': adminToken
        },
        body: JSON.stringify({
          amount: finalAmount,
          description: creditDescription
        })
      })

      const result = await response.json()

      if (result.success) {
        setSuccess(`成功${operation === 'add' ? '增加' : '扣除'} ${amount} 个积分`)
        setSelectedUser(result.data.user)
        setCreditAmount('')
        setCreditDescription('')
        // Refresh stats
        verifyAndLoadData(adminToken)
        // Refresh transactions if showing
        if (showTransactions) {
          loadUserTransactions()
        }
      } else {
        setError(result.error || '积分调整失败')
      }
    } catch (error) {
      setError('积分调整失败')
    } finally {
      setCreditLoading(false)
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">
              管理员登录
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              输入管理员访问令牌以继续
            </p>
          </div>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border">
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            <div className="space-y-6">
              <div>
                <label htmlFor="token" className="block text-sm font-medium text-gray-700">
                  管理员令牌
                </label>
                <div className="mt-1 relative">
                  <input
                    type={showToken ? 'text' : 'password'}
                    value={adminToken}
                    onChange={(e) => setAdminToken(e.target.value)}
                    placeholder="输入管理员访问令牌"
                    className="input pr-10"
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowToken(!showToken)}
                  >
                    {showToken ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              <button
                onClick={handleLogin}
                disabled={loading}
                className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <LoadingSpinner size="sm" /> : '登录管理面板'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Sku Studio 管理面板
              </h1>
            </div>
            <button
              onClick={handleLogout}
              className="btn-secondary"
            >
              退出登录
            </button>
          </div>
          
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'dashboard'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Settings className="w-4 h-4" />
                  <span>系统概况</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('credits')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'credits'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <UserPlus className="w-4 h-4" />
                  <span>积分管理</span>
                </div>
              </button>
              <button
                onClick={() => {
                  setActiveTab('redemption')
                  if (activeTab !== 'redemption') {
                    loadRedemptionCodes(1)
                  }
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'redemption'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Gift className="w-4 h-4" />
                  <span>兑换码管理</span>
                </div>
              </button>
            </nav>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            {error}
          </div>
        )}

        {activeTab === 'dashboard' && (
          <>
            {/* Stats Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">用户统计</h3>
                <p className="text-gray-600">
                  总计: {stats?.users.total || 0} | 已验证: {stats?.users.verified || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center space-x-3">
              <div className="bg-green-100 p-3 rounded-full">
                <CreditCard className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">交易统计</h3>
                <p className="text-gray-600">
                  总数: {stats?.transactions.total || 0} | 收入: ¥{(stats?.transactions.revenue || 0) / 100}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-3 rounded-full">
                <Image className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">图片统计</h3>
                <p className="text-gray-600">
                  总数: {stats?.images.total || 0} | 已用积分: {stats?.images.creditsUsed || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Settings */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-gray-500" />
              <h2 className="text-xl font-semibold text-gray-900">系统设置</h2>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {settings && Object.entries(settings).map(([key, value]) => (
                <div key={key} className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    {key === 'initial_credits' && '新用户初始积分'}
                    {key === 'credit_price_cents' && '积分价格（分/积分）'}
                    {key === 'image_generation_cost' && '生成图片成本（积分）'}
                    {key === 'max_image_size_mb' && '最大图片大小（MB）'}
                    {key === 'allowed_image_types' && '允许的图片类型'}
                    {key === 'default_prompt' && '默认图片生成提示词'}
                  </label>
                  <div className={`${key === 'default_prompt' ? 'space-y-2' : 'flex space-x-2'}`}>
                    {key === 'default_prompt' ? (
                      <>
                        <textarea
                          value={value}
                          onChange={(e) => setSettings({
                            ...settings,
                            [key]: e.target.value
                          })}
                          rows={3}
                          className="w-full input resize-none"
                        />
                        <button
                          onClick={() => updateSetting(key, value)}
                          className="btn-primary px-3 py-2 self-start"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          保存
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={value}
                          onChange={(e) => setSettings({
                            ...settings,
                            [key]: e.target.value
                          })}
                          className="flex-1 input"
                        />
                        <button
                          onClick={() => updateSetting(key, value)}
                          className="btn-primary px-3 py-2"
                        >
                          <Save className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
          </>
        )}

        {activeTab === 'credits' && (
          <div className="space-y-6">
            {/* User Search */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center space-x-2 mb-4">
                <Search className="w-5 h-5 text-gray-500" />
                <h2 className="text-xl font-semibold text-gray-900">用户搜索</h2>
              </div>
              
              <div className="flex space-x-4">
                <div className="flex-1">
                  <input
                    type="email"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder="输入用户邮箱地址"
                    className="w-full input"
                    onKeyPress={(e) => e.key === 'Enter' && searchUser()}
                  />
                </div>
                <button
                  onClick={searchUser}
                  disabled={searchLoading}
                  className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searchLoading ? <LoadingSpinner size="sm" /> : '搜索'}
                </button>
              </div>
            </div>

            {/* User Info */}
            {selectedUser && (
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    <h2 className="text-xl font-semibold text-gray-900">用户信息</h2>
                  </div>
                  <button
                    onClick={loadUserTransactions}
                    className="btn-secondary text-sm"
                  >
                    查看交易记录
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">邮箱地址</label>
                    <p className="mt-1 text-gray-900">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">当前积分</label>
                    <p className="mt-1 text-2xl font-bold text-blue-600">{selectedUser.credits}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">验证状态</label>
                    <p className={`mt-1 ${selectedUser.is_verified ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedUser.is_verified ? '已验证' : '未验证'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">注册时间</label>
                    <p className="mt-1 text-gray-900">
                      {new Date(selectedUser.created_at).toLocaleString('zh-CN')}
                    </p>
                  </div>
                </div>

                {/* Credit Adjustment */}
                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">积分调整</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          积分数量
                        </label>
                        <input
                          type="number"
                          value={creditAmount}
                          onChange={(e) => setCreditAmount(e.target.value)}
                          placeholder="输入积分数量"
                          min="1"
                          className="w-full input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          调整说明
                        </label>
                        <input
                          type="text"
                          value={creditDescription}
                          onChange={(e) => setCreditDescription(e.target.value)}
                          placeholder="请输入调整原因"
                          className="w-full input"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-4">
                      <button
                        onClick={() => adjustCredits('add')}
                        disabled={creditLoading || !creditAmount || !creditDescription}
                        className="flex items-center space-x-2 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {creditLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <Plus className="w-4 h-4" />
                            <span>增加积分</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => adjustCredits('subtract')}
                        disabled={creditLoading || !creditAmount || !creditDescription}
                        className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {creditLoading ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <>
                            <Minus className="w-4 h-4" />
                            <span>扣除积分</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Transaction History */}
            {showTransactions && userTransactions.length > 0 && (
              <div className="bg-white p-6 rounded-lg shadow-sm border">
                <div className="flex items-center space-x-2 mb-4">
                  <CreditCard className="w-5 h-5 text-gray-500" />
                  <h2 className="text-xl font-semibold text-gray-900">交易记录</h2>
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          时间
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          类型
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          数量
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          说明
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userTransactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {new Date(transaction.created_at).toLocaleString('zh-CN')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              transaction.type === 'bonus' 
                                ? 'bg-green-100 text-green-800'
                                : transaction.type === 'purchase'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {transaction.type === 'bonus' && '奖励'}
                              {transaction.type === 'purchase' && '购买'}
                              {transaction.type === 'usage' && '使用'}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                            transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {transaction.amount > 0 ? '+' : ''}{transaction.amount}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900">
                            {transaction.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'redemption' && (
          <div className="space-y-6">
            {/* Create Redemption Codes */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center space-x-2 mb-4">
                <Plus className="w-5 h-5 text-gray-500" />
                <h2 className="text-xl font-semibold text-gray-900">生成兑换码</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    积分数量 *
                  </label>
                  <input
                    type="number"
                    value={newCodeCredits}
                    onChange={(e) => setNewCodeCredits(e.target.value)}
                    placeholder="兑换积分数量"
                    min="1"
                    className="w-full input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    使用次数 *
                  </label>
                  <input
                    type="number"
                    value={newCodeMaxUses}
                    onChange={(e) => setNewCodeMaxUses(e.target.value)}
                    placeholder="最大使用次数"
                    min="1"
                    className="w-full input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    生成数量 *
                  </label>
                  <input
                    type="number"
                    value={newCodeCount}
                    onChange={(e) => setNewCodeCount(e.target.value)}
                    placeholder="生成数量 (1-100)"
                    min="1"
                    max="100"
                    className="w-full input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    描述信息
                  </label>
                  <input
                    type="text"
                    value={newCodeDescription}
                    onChange={(e) => setNewCodeDescription(e.target.value)}
                    placeholder="兑换码描述（可选）"
                    className="w-full input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    过期时间
                  </label>
                  <input
                    type="datetime-local"
                    value={newCodeExpiresAt}
                    onChange={(e) => setNewCodeExpiresAt(e.target.value)}
                    className="w-full input"
                  />
                </div>
              </div>

              <button
                onClick={createRedemptionCodes}
                disabled={createCodeLoading || !newCodeCredits || !newCodeMaxUses || !newCodeCount}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createCodeLoading ? <LoadingSpinner size="sm" /> : '生成兑换码'}
              </button>
            </div>

            {/* Redemption Codes List */}
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <Gift className="w-5 h-5 text-gray-500" />
                  <h2 className="text-xl font-semibold text-gray-900">兑换码列表</h2>
                </div>
                {selectedCodes.length > 0 && (
                  <button
                    onClick={batchDeactivateCodes}
                    className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>批量禁用 ({selectedCodes.length})</span>
                  </button>
                )}
              </div>

              {redemptionLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left">
                            <input
                              type="checkbox"
                              checked={selectedCodes.length === redemptionCodes.filter(code => code.isActive).length && redemptionCodes.filter(code => code.isActive).length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedCodes(redemptionCodes.filter(code => code.isActive).map(code => code.id))
                                } else {
                                  setSelectedCodes([])
                                }
                              }}
                              className="rounded border-gray-300"
                            />
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            兑换码
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            积分
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            使用情况
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            状态
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            创建时间
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            操作
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {redemptionCodes.map((code) => (
                          <tr key={code.id} className={!code.isActive ? 'bg-gray-50' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {code.isActive && (
                                <input
                                  type="checkbox"
                                  checked={selectedCodes.includes(code.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setSelectedCodes([...selectedCodes, code.id])
                                    } else {
                                      setSelectedCodes(selectedCodes.filter(id => id !== code.id))
                                    }
                                  }}
                                  className="rounded border-gray-300"
                                />
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <span className={`font-mono text-sm ${!code.isActive ? 'text-gray-400' : 'text-gray-900'}`}>
                                  {code.code}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(code.code)}
                                  className="text-blue-600 hover:text-blue-700"
                                  title="复制兑换码"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                              {code.description && (
                                <p className="text-xs text-gray-500 mt-1">{code.description}</p>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                              {code.credits}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div className="flex items-center space-x-2">
                                <span>{code.usedCount} / {code.maxUses}</span>
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-blue-600 h-2 rounded-full"
                                    style={{ width: `${(code.usedCount / code.maxUses) * 100}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                code.isActive
                                  ? code.usedCount >= code.maxUses
                                    ? 'bg-gray-100 text-gray-800'
                                    : 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {!code.isActive ? '已禁用' :
                                 code.usedCount >= code.maxUses ? '已用完' : '可使用'}
                              </span>
                              {code.expiresAt && (
                                <div className="text-xs text-gray-500 mt-1">
                                  <Calendar className="w-3 h-3 inline mr-1" />
                                  {new Date(code.expiresAt) < new Date() ? '已过期' : '未过期'}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(code.createdAt).toLocaleString('zh-CN')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                              {code.isActive && (
                                <button
                                  onClick={() => deactivateCode(code.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  禁用
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {redemptionPagination.pages > 1 && (
                    <div className="flex items-center justify-between mt-6">
                      <div className="text-sm text-gray-700">
                        显示第 {((redemptionPagination.page - 1) * redemptionPagination.limit) + 1} 到 {Math.min(redemptionPagination.page * redemptionPagination.limit, redemptionPagination.total)} 条，共 {redemptionPagination.total} 条
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => loadRedemptionCodes(redemptionPagination.page - 1)}
                          disabled={redemptionPagination.page <= 1}
                          className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          上一页
                        </button>
                        <span className="px-3 py-1 text-sm">
                          第 {redemptionPagination.page} 页 / 共 {redemptionPagination.pages} 页
                        </span>
                        <button
                          onClick={() => loadRedemptionCodes(redemptionPagination.page + 1)}
                          disabled={redemptionPagination.page >= redemptionPagination.pages}
                          className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          下一页
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  )
}